import { Box, Typography, Grid, Card, Autocomplete, TextField, Stack } from "@mui/material";
import { inputRowCommonStyles } from "../../../../../common/formStyles";
import AgendaList from "./AgendaList";
import AgendaPreview from "./AgendaPreview";
import { AgendaSelectionViewProps } from "./type";
import CustomBtnFilled from "../../../../../atoms/form-molecules/CustomBtnFilled";
import { useHookFormContext } from "../../../../../utils/hooks/useHookFormContext";
import { useState } from "react";
import { agendaFilterOptions } from "./helper";


const AgendaSelectionView = ({
  agendas,
  selectedAgendaId,
  setSelectedAgendaId,
}: AgendaSelectionViewProps) => {
  const [activeFilters, setActiveFilters] = useState([{ label: "All", value: "all" }]);
  const { registerState, setValue, watch } = useHookFormContext();
  const selectedAgenda = agendas.find(agenda => agenda.id === selectedAgendaId);
  const selectedAgendaIds = watch("selected_agendas") || [];
  const filteredAgendas = agendas.filter((agenda) => {
    if (activeFilters.some(f => f.value === "all")) return true;
    return activeFilters.some((filter) => {
      if (filter.value === "declined") return agenda.status === 'Declined';
      if (filter.value === "responded-by-expert") return agenda.status === 'RespondedByExpert';
      if (filter.value === "responded-by-admin") return agenda.status === 'RespondedByAdmin';
      if (filter.value === 'new-agenda') return agenda.status === 'NewAgenda';
      if (filter.value === 'shared') return agenda.status === 'Sent';
      return false;
    });
  });

  const allIds = filteredAgendas.map(a => a.id); // Only filtered agendas
  const allSelected = allIds.length > 0 && allIds.every(id => selectedAgendaIds.includes(id));

  const handleToggleSelectAll = () => {
    if (allSelected) {
      setValue("selected_agendas", [], { shouldDirty: true, shouldTouch: true });
      setSelectedAgendaId(null);
    } else {
      setValue("selected_agendas", allIds, { shouldDirty: true, shouldTouch: true });
      if (allIds.length > 0) {
        setSelectedAgendaId(allIds[0]);
      }
    }
  };
  return (
    <Box sx={inputRowCommonStyles}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
          Select Agendas: *
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" >
          <CustomBtnFilled
            label={allSelected ? "Remove All" : "Select All"}
            variant="contained"
            onClick={handleToggleSelectAll}
          />
          <Autocomplete
            multiple
            options={agendaFilterOptions}
            size="small"
            sx={{ minWidth: 250 }}
            getOptionLabel={(option) => option.label}
            defaultValue={[agendaFilterOptions[0]]}
            filterSelectedOptions
            disableCloseOnSelect
            onChange={(_, newValue) => {
              setActiveFilters(newValue);
              // Reset selected agendas on filter change
              setValue("selected_agendas", [], { shouldDirty: true, shouldTouch: true });
              setSelectedAgendaId(null);
            }}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Filter Options"
                aria-placeholder="Filter Options"
                placeholder="Add Options"
              />
            )}
          />
        </Stack>

      </Box>
      <Grid container spacing={0} sx={{ flex: 1, height: '400px', overflow: 'hidden', margin: 0, width: '100%' }}>
        {/* Left Sidebar */}
        <Grid item xs={12} md={4} sx={{ height: '100%', paddingLeft: '0 !important', paddingTop: '0 !important' }}>
          <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '10px' }}>
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
              <Typography fontSize={15} fontWeight={600}>
                Available Agendas
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                ({filteredAgendas.length})
              </Typography>
            </Box>
            <AgendaList
              agendas={filteredAgendas}
              selectedAgendaId={selectedAgendaId}
              setSelectedAgendaId={setSelectedAgendaId}
              registerState={registerState}
            />
          </Card>
        </Grid>

        {/* Right Section */}
        <Grid item xs={12} md={8} sx={{ height: '100%', paddingLeft: '16px !important', paddingTop: '0 !important' }}>
          <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '10px' }}>
            <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
              <Typography fontWeight={600} fontSize={15}>
                Agenda Preview
              </Typography>
            </Box>
            <AgendaPreview agenda={selectedAgenda} />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AgendaSelectionView;